import ClockoutController from '../../core/clockout.controller';
import Inherit from '../../core/inherit';
import RolesDelegate from './roles.delegate';

class RolesController extends Inherit(ClockoutController, RolesDelegate) {
    state = {
        loading: true,
        roles: [],
        catalog: [],
        selectedRoleId: null,
        pendingScopeIds: null,
        saving: false,
        newRoleModalOpen: false,
        newRoleName: '',
        newRoleDescription: '',
        newRoleCloneFromId: null,
        creatingRole: false,
        scopeSearch: '',
        editRoleModalOpen: false,
        editingRole: null,
        editingRoleSubmitting: false,
    };

    async initialize() {
        if (!this.user || !this.user.access || this.user.access.length === 0) {
            this.navigateLogin();
            return;
        }
        await this.loadRoles();
    }

    async loadRoles() {
        this.setState({ loading: true });
        const [roles, catalog] = await Promise.all([
            this.data.fetchRoles(),
            this.data.fetchScopesCatalog(),
        ]);
        // Owner role first, then everything else in the order the API returned.
        const ordered = [...roles].sort((a, b) => (b.is_owner_role ? 1 : 0) - (a.is_owner_role ? 1 : 0));
        this.setState((prev) => ({
            roles: ordered,
            catalog,
            loading: false,
            selectedRoleId: prev.selectedRoleId && ordered.some((r) => r.id === prev.selectedRoleId)
                ? prev.selectedRoleId
                : (ordered[0]?.id || null),
        }));
    }

    getSelectedRole() {
        return this.state.roles.find((r) => r.id === this.state.selectedRoleId) || null;
    }

    /** The scope_ids currently shown for the selected role: the unsaved
     * pending edit if there is one, otherwise the last-committed set. */
    getEffectiveScopeIds() {
        if (this.state.pendingScopeIds) return this.state.pendingScopeIds;
        return this.getSelectedRole()?.scope_ids || [];
    }

    isDirty() {
        if (!this.state.pendingScopeIds) return false;
        const committed = this.getSelectedRole()?.scope_ids || [];
        const a = [...this.state.pendingScopeIds].sort().join(',');
        const b = [...committed].sort().join(',');
        return a !== b;
    }

    handleSaveScopes = async () => {
        const role = this.getSelectedRole();
        if (!role || !this.isDirty()) return;
        const scopeIds = this.state.pendingScopeIds;
        try {
            this.setState({ saving: true });
            await this.data.setRoleScopes(role.id, scopeIds);
            this.setState({ pendingScopeIds: null });
            await this.loadRoles();
            this.props.notificationApi.success({
                message: 'Scopes updated',
                description: `"${role.name}" now has ${scopeIds.length} scope(s).`,
            });
        } catch (error) {
            console.error('Error saving role scopes:', error);
            const description = error?.response?.data?.error || 'Failed to update scopes. Please try again.';
            this.props.notificationApi.error({ message: 'Update failed', description });
        } finally {
            this.setState({ saving: false });
        }
    };

    handleDiscardChanges = () => {
        this.setState({ pendingScopeIds: null });
    };

    handleCreateRole = async () => {
        const { newRoleName, newRoleDescription, newRoleCloneFromId } = this.state;
        if (!newRoleName || !newRoleName.trim()) return;
        try {
            this.setState({ creatingRole: true });
            const result = await this.data.createRole(newRoleName.trim(), newRoleCloneFromId, newRoleDescription?.trim() || null);
            this.setNewRoleModalOpen(false);
            await this.loadRoles();
            if (result?.role?.id) this.selectRole(result.role.id);
            this.props.notificationApi.success({
                message: 'Role created',
                description: `"${newRoleName.trim()}" is ready to configure.`,
            });
        } catch (error) {
            console.error('Error creating role:', error);
            const description = error?.response?.data?.error || 'Failed to create role. Please try again.';
            this.props.notificationApi.error({ message: 'Create failed', description });
        } finally {
            this.setState({ creatingRole: false });
        }
    };

    handleRenameRole = async (roleId, name) => {
        try {
            await this.data.renameRole(roleId, name);
            await this.loadRoles();
            this.props.notificationApi.success({ message: 'Role renamed' });
        } catch (error) {
            console.error('Error renaming role:', error);
            const description = error?.response?.data?.error || 'Failed to rename role. Please try again.';
            this.props.notificationApi.error({ message: 'Rename failed', description });
        }
    };

    handleDeleteRole = async (roleId) => {
        try {
            await this.data.deleteRole(roleId);
            if (this.state.selectedRoleId === roleId) {
                this.setState({ selectedRoleId: null, pendingScopeIds: null });
            }
            await this.loadRoles();
            this.props.notificationApi.success({ message: 'Role deleted' });
        } catch (error) {
            console.error('Error deleting role:', error);
            const description = error?.response?.data?.error || 'Failed to delete role. Please try again.';
            this.props.notificationApi.error({ message: 'Delete failed', description });
        }
    };

    handleSetDefaultRole = async (roleId) => {
        try {
            await this.data.setDefaultRole(roleId);
            await this.loadRoles();
            this.props.notificationApi.success({ message: 'Default role updated' });
        } catch (error) {
            console.error('Error setting default role:', error);
            const description = error?.response?.data?.error || 'Failed to set default role. Please try again.';
            this.props.notificationApi.error({ message: 'Update failed', description });
        }
    };

    handleEditRole = (role) => {
        this.setState({
            editingRole: { ...role },
            editRoleModalOpen: true,
        });
    };

    setEditRoleModalOpen = (open) => {
        this.setState({ editRoleModalOpen: open });
        if (!open) {
            this.setState({ editingRole: null, editingRoleSubmitting: false });
        }
    };

    setEditingRoleName = (name) => {
        this.setState((prev) => ({
            editingRole: prev.editingRole ? { ...prev.editingRole, name } : null,
        }));
    };

    setEditingRoleDescription = (description) => {
        this.setState((prev) => ({
            editingRole: prev.editingRole ? { ...prev.editingRole, description } : null,
        }));
    };

    handleEditRoleSubmit = async ({ roleId, name, description }) => {
        if (!roleId || !name || !name.trim()) return;
        try {
            this.setState({ editingRoleSubmitting: true });
            await this.data.updateRole(roleId, name.trim(), description);
            this.setEditRoleModalOpen(false);
            await this.loadRoles();
            this.props.notificationApi.success({
                message: 'Role updated',
                description: `"${name.trim()}" has been updated.`,
            });
        } catch (error) {
            console.error('Error updating role:', error);
            const description = error?.response?.data?.error || 'Failed to update role. Please try again.';
            this.props.notificationApi.error({ message: 'Update failed', description });
        } finally {
            this.setState({ editingRoleSubmitting: false });
        }
    };
}

export default RolesController;
